import React, { createContext, useContext, useState, useCallback } from 'react';

const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

// Simulation modes
export const SIMULATION_MODES = {
  NORMAL: 'normal',
  FALL: 'fall',
  PROLONGED_ABSENCE: 'prolonged_absence',
  NIGHT: 'night',
};

// Zone types
export const ZONE_TYPES = {
  BATHROOM: 'bathroom',
  BEDROOM: 'bedroom',
  HALLWAY: 'hallway',
  LIVING_ROOM: 'living_room',
};

// Event types
export const EVENT_TYPES = {
  SAFE: 'safe',
  ALERT: 'alert',
  SOS: 'sos',
  FALL_DETECTED: 'fall_detected',
  INACTIVITY: 'inactivity',
  EXIT: 'exit',
  ENTRY: 'entry',
};

export const DashboardProvider = ({ children }) => {
  const [simulationMode, setSimulationMode] = useState(SIMULATION_MODES.NORMAL);
  const [activeZone, setActiveZone] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isMonitoring, setIsMonitoring] = useState(true);

  // Zone data with thermal readings
  const [zones, setZones] = useState({
    [ZONE_TYPES.BATHROOM]: {
      id: ZONE_TYPES.BATHROOM,
      name: 'Bathroom',
      icon: '🚿',
      occupancy: 0,
      temperature: 22.5,
      lastActivity: null,
      status: EVENT_TYPES.SAFE,
      thermalData: [],
    },
    [ZONE_TYPES.BEDROOM]: {
      id: ZONE_TYPES.BEDROOM,
      name: 'Bedroom',
      icon: '🛏️',
      occupancy: 0,
      temperature: 23.0,
      lastActivity: null,
      status: EVENT_TYPES.SAFE,
      thermalData: [],
    },
    [ZONE_TYPES.HALLWAY]: {
      id: ZONE_TYPES.HALLWAY,
      name: 'Hallway',
      icon: '🚶',
      occupancy: 0,
      temperature: 22.0,
      lastActivity: null,
      status: EVENT_TYPES.SAFE,
      thermalData: [],
    },
  });

  // Recent events
  const [events, setEvents] = useState([]);

  // Set simulation mode
  const setMode = useCallback((mode) => {
    setSimulationMode(mode);
    
    // Update zones based on simulation mode
    setZones((prevZones) => {
      const updatedZones = { ...prevZones };
      
      Object.keys(updatedZones).forEach((zoneId) => {
        const zone = updatedZones[zoneId];
        
        switch (mode) {
          case SIMULATION_MODES.NORMAL:
            zone.occupancy = zoneId === ZONE_TYPES.BATHROOM ? 1 : 0;
            zone.temperature = 22.5 + Math.random() * 2;
            zone.status = EVENT_TYPES.SAFE;
            break;
          case SIMULATION_MODES.FALL:
            if (zoneId === ZONE_TYPES.BATHROOM) {
              zone.occupancy = 1;
              zone.temperature = 25.0;
              zone.status = EVENT_TYPES.FALL_DETECTED;
            }
            break;
          case SIMULATION_MODES.PROLONGED_ABSENCE:
            zone.occupancy = 0;
            zone.temperature = 21.0;
            zone.status = EVENT_TYPES.INACTIVITY;
            break;
          case SIMULATION_MODES.NIGHT:
            zone.occupancy = zoneId === ZONE_TYPES.BEDROOM ? 1 : 0;
            zone.temperature = 20.0;
            zone.status = EVENT_TYPES.SAFE;
            break;
          default:
            break;
        }
      });
      
      return updatedZones;
    });
  }, []);

  // Update zone thermal data
  const updateZoneThermalData = useCallback((zoneId, data) => {
    setZones((prevZones) => ({
      ...prevZones,
      [zoneId]: {
        ...prevZones[zoneId],
        thermalData: data,
      },
    }));
  }, []);

  // Add event
  const addEvent = useCallback((event) => {
    const newEvent = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...event,
    };
    
    setEvents((prevEvents) => [newEvent, ...prevEvents].slice(0, 100)); // Keep last 100 events
  }, []);

  // Select event and show modal
  const selectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  }, []);

  // Close event modal
  const closeEventModal = useCallback(() => {
    setShowEventModal(false);
    setSelectedEvent(null);
  }, []);

  // Toggle monitoring
  const toggleMonitoring = useCallback(() => {
    setIsMonitoring((prev) => !prev);
  }, []);

  const value = {
    simulationMode,
    setMode,
    activeZone,
    setActiveZone,
    zones,
    setZones,
    updateZoneThermalData,
    events,
    addEvent,
    showEventModal,
    selectEvent,
    closeEventModal,
    selectedEvent,
    isMonitoring,
    toggleMonitoring,
    SIMULATION_MODES,
    ZONE_TYPES,
    EVENT_TYPES,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};